import type {
  AwardBulkChanges,
  AwardBulkUpdateRequest,
  AwardBulkUpdateResponse,
  AwardDictionaries,
  AwardDictionaryPruneResponse,
  AwardExportRequest,
  AwardExportResult,
  AwardFilterOptionsResponse,
  AwardMatchRequest,
  AwardMatchResponse,
  AwardResponse,
  AwardWriteBody,
  AwardsListResponse,
  AwardsQueryParams,
} from "./awards.types.js";
import * as api from "./client.js";
import { readDownload } from "./fileDownload.js";

const EXPORT_FALLBACK_FILENAME = "awards-export.xlsx";

/** The filter dropdowns carry a string value per filter, so "no filter" arrives
 * as the literal "all". It must never reach the API. */
const ALL_SENTINEL = "all";

type FilterValue = string | number;

/**
 * Appends one filter as a CSV parameter (`status=winner,shortlisted`), dropping
 * the values that all mean "no filter": `undefined`, an empty string, an empty
 * array, and the `"all"` sentinel. A filter that resolves to nothing produces no
 * parameter at all rather than an empty one, which the backend would have to
 * special-case.
 */
function appendFilter(
  params: URLSearchParams,
  key: string,
  value: FilterValue | FilterValue[] | undefined,
): void {
  if (value === undefined) return;

  const values = (Array.isArray(value) ? value : [value])
    .map(entry => (typeof entry === "string" ? entry.trim() : entry.toString()))
    .filter(entry => entry !== "" && entry !== ALL_SENTINEL);

  if (values.length === 0) return;

  params.append(key, values.join(","));
}

export const awards = {
  getAll: async (filters?: AwardsQueryParams): Promise<AwardsListResponse> => {
    const params = new URLSearchParams();

    if (filters?.limit !== undefined) params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined) params.append("offset", filters.offset.toString());

    appendFilter(params, "product", filters?.product);
    appendFilter(params, "awardType", filters?.awardType);
    appendFilter(params, "series", filters?.series);
    appendFilter(params, "status", filters?.status);
    appendFilter(params, "shortlistTag", filters?.shortlistTag);
    appendFilter(params, "state", filters?.state);
    appendFilter(params, "year", filters?.year);
    appendFilter(params, "countryId", filters?.countryId);
    appendFilter(params, "companyId", filters?.companyId);
    appendFilter(params, "contactId", filters?.contactId);

    // A flag, not a list: awards-api narrows only on the literal "true", and treats
    // "false" exactly like an absent parameter, so nothing but true ever goes out.
    if (filters?.incomplete === true) params.append("incomplete", "true");

    const query = params.toString() ? `?${params.toString()}` : "";
    return api.get<AwardsListResponse>(`/v1/awards${query}`);
  },
  getFilterOptions: async (): Promise<AwardFilterOptionsResponse> => {
    return api.get<AwardFilterOptionsResponse>("/v1/awards/filter-options");
  },
  /**
   * Attaches an Address Book id to one side of a row the importer could not
   * match. The body is passed straight through: it is partial by contract, so
   * building it is the caller's job and an omitted key must stay omitted rather
   * than becoming an explicit `undefined` the serialiser drops for a different
   * reason.
   */
  match: async (id: string, body: AwardMatchRequest): Promise<AwardMatchResponse> => {
    return api.patch<AwardMatchResponse>(`/v1/awards/${id}/match`, body);
  },
  /** The full dictionaries, not `getFilterOptions`: the form has to offer every
   * product and series that exists, including the ones no award uses yet. */
  getDictionaries: async (): Promise<AwardDictionaries> => {
    return api.get<AwardDictionaries>("/v1/awards/dictionaries");
  },
  pruneDictionaries: async (): Promise<AwardDictionaryPruneResponse> => {
    return api.post<AwardDictionaryPruneResponse>("/v1/awards/dictionaries/prune");
  },
  getById: async (id: string): Promise<AwardResponse> => {
    return api.get<AwardResponse>(`/v1/awards/${id}`);
  },
  create: async (body: AwardWriteBody): Promise<AwardResponse> => {
    return api.post<AwardResponse>("/v1/awards", body);
  },
  update: async (id: string, body: AwardWriteBody): Promise<AwardResponse> => {
    return api.put<AwardResponse>(`/v1/awards/${id}`, body);
  },
  remove: async (id: string): Promise<void> => {
    await api.del(`/v1/awards/${id}`);
  },
  /**
   * Applies one partial change to every listed award. `changes` goes out as the
   * caller built it: an omitted key must stay omitted, since the server treats
   * every present key as a column to write.
   */
  bulkUpdate: async (
    ids: string[],
    changes: AwardBulkChanges,
  ): Promise<AwardBulkUpdateResponse> => {
    const body: AwardBulkUpdateRequest = { ids, changes };
    return api.patch<AwardBulkUpdateResponse>("/v1/awards/bulk", body);
  },
  /**
   * Downloads the awards workbook. No ids, or an empty list, means every row:
   * the key is left off the body entirely rather than sent as `[]`, so the two
   * spellings of "everything" do not both travel the wire.
   */
  export: async (ids?: string[]): Promise<AwardExportResult> => {
    const body: AwardExportRequest = ids && ids.length > 0 ? { ids } : {};
    const response = await api.fetchRaw("/v1/awards/export", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    return readDownload(response, EXPORT_FALLBACK_FILENAME);
  },
};
